import React, { useState, useEffect } from 'react';
import { MapPin, Trash2, AlertTriangle, Building2, RefreshCw, Copy, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGISTER_HOST = 'daftar.kumarahotspot.com';
const copyRegisterLink = (slug) => {
  if (!slug) {
    toast.error('Slug cabang belum tersedia. Simpan ulang cabang.');
    return;
  }
  const url = `https://${REGISTER_HOST}/${slug}`;
  navigator.clipboard.writeText(url).then(
    () => toast.success('Link disalin: ' + url),
    () => toast.error('Gagal menyalin link'),
  );
};

const BranchManagement = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    branch: null
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await axios.get(`${API_URL}/api/v2/superadmin/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const branchId = deleteDialog.branch.id;
    
    setDeleteDialog({ open: false, branch: null });
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        // Use V2 MySQL endpoint
        `${API_URL}/api/v2/superadmin/branches/${branchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Branch deleted successfully!');
        fetchBranches();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Failed to delete branch: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">📍 Branch Management</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage all branches across all tenants
          </p>
        </div>
        <Button onClick={fetchBranches} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Branches</p>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{branches.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branches List */}
      <Card>
        <CardHeader>
          <CardTitle>All Branches</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3 dark:text-slate-500" />
              <p className="text-slate-500 dark:text-slate-400">Loading branches...</p>
            </div>
          ) : branches.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">No branches found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex-wrap gap-3"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-[280px]">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {branch.name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 mt-1">
                        <span>📍 {branch.address || 'No address'}</span>
                        <span>•</span>
                        <span>🏢 {branch.tenant_name || 'No tenant'}</span>
                      </div>
                      {/* Link Daftar Publik */}
                      <div className="mt-2 flex items-center gap-2 py-1.5 px-2 rounded-md bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 max-w-lg">
                        <Link2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] uppercase tracking-wide text-indigo-500 dark:text-indigo-400">Link Daftar Publik</p>
                          <p className="text-xs font-mono text-indigo-700 dark:text-indigo-300 truncate" title={branch.slug ? `https://${REGISTER_HOST}/${branch.slug}` : ''}>
                            {branch.slug ? `${REGISTER_HOST}/${branch.slug}` : '— slug belum tersedia —'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyRegisterLink(branch.slug)}
                          disabled={!branch.slug}
                          className="h-7 px-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border-indigo-200 dark:border-indigo-800"
                          data-testid={`bm-copy-register-link-${branch.id}`}
                          title="Salin link pendaftaran publik"
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400"
                    onClick={() => setDeleteDialog({ open: true, branch })}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Delete Branch?
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                Branch Details:
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <strong>Name:</strong> {deleteDialog.branch?.name}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <strong>Tenant:</strong> {deleteDialog.branch?.tenant_name}
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                ⚠️ Branch cannot be deleted if it has customers assigned to it.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteDialog({ open: false, branch: null })}
                className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all"
              >
                Delete Branch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchManagement;
